import React, { useEffect, useState, useCallback } from 'react';
import {Modal, Button, Space, message, Switch, Tooltip, Alert, Divider} from 'antd';
import { InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import QRCode from 'react-qr-code';
import { copyToClipboard } from '../../helpers/clipboard';
import ShortUrlStatsModal from '../ShortUrlStatsModal';
import Cart from '../Header/Cart';
import ButtonGroup from "antd/es/button/button-group";

interface ShortUrlModalProps {
  /** 是否显示弹框 */
  open: boolean;
  /** 关闭弹框的回调 */
  onCancel: () => void;
  /** 要生成短网址的原始URL */
  url: string;
}

/**
 * 短网址弹框组件
 * 
 * @author dongdong.fdd
 * @date 2025/01/XX
 */
const ShortUrlModal: React.FC<ShortUrlModalProps> = ({
  open,
  onCancel,
  url,
}) => {
  const [shortUrl, setShortUrl] = useState<string>('');
  const [shortId, setShortId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isVip, setIsVip] = useState(false);
  const [enableStats, setEnableStats] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [showCart, setShowCart] = useState(false);

  // 创建短网址
  const createShortUrl = useCallback(async () => {
    if (!url) {
      message.error('URL 不能为空');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/u/save?url=${encodeURIComponent(url)}`, {
        method: 'POST',
      });
      const json = await response.json();
      
      if (json && json.shortId) {
        const generatedShortUrl = `https://z.wiki/u/${json.shortId}`;
        setShortUrl(generatedShortUrl);
        setShortId(json.shortId);
        // 获取统计状态（如果后端返回了enableStats字段）
        if (json.enableStats !== undefined) {
          setEnableStats(json.enableStats);
        } else {
          // 默认关闭
          setEnableStats(false);
        }
      } else {
        message.error('创建短网址失败，请稍后重试');
      }
    } catch (error) {
      console.error('创建短网址失败:', error);
      message.error('创建短网址失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [url]);

  // 获取用户VIP状态
  useEffect(() => {
    fetch('/login/info')
      .then(res => res.json())
      .then(data => {
        if (data.isSuccess && data.detail) {
          setIsVip(data.detail.isActiveVip || false);
        }
      })
      .catch(err => {
        console.error('获取用户信息失败:', err);
      });
  }, []);

  // 当弹框打开时，自动创建短网址
  useEffect(() => {
    if (open && url) {
      setShortUrl('');
      setShortId('');
      setEnableStats(false);
      createShortUrl();
    }
  }, [open, url, createShortUrl]);

  // 处理复制短网址
  const handleCopy = () => {
    if (shortUrl) {
      copyToClipboard(shortUrl, '短网址已复制到剪贴板');
      onCancel();
    }
  };

  // 处理开启/关闭统计
  const handleToggleStats = async (checked: boolean) => {
    if (!isVip) {
      Modal.warning({
        title: '仅限VIP用户',
        content: '此功能仅限VIP用户使用，请先开通VIP',
      });
      return;
    }

    if (!shortId) {
      message.error('短网址ID不存在');
      return;
    }

    setStatsLoading(true);
    try {
      const endpoint = checked ? '/u/enableStats' : '/u/disableStats';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `shortId=${encodeURIComponent(shortId)}`,
      });
      const json = await response.json();
      
      if (json.success) {
        setEnableStats(checked);
        message.success(json.message || (checked ? '统计功能已开启' : '统计功能已关闭'));
      } else {
        message.error(json.message || '操作失败');
      }
    } catch (error) {
      console.error('操作失败:', error);
      message.error('操作失败，请稍后重试');
    } finally {
      setStatsLoading(false);
    }
  };

  // 处理查看统计
  const handleViewStats = () => {
    if (!isVip) {
      Modal.warning({
        title: '仅限VIP用户',
        content: '此功能仅限VIP用户使用，请先开通VIP',
      });
      return;
    }

    if (!enableStats) {
      message.warning('请先开启统计功能');
      return;
    }

    setStatsModalOpen(true);
  };

  return (
    <Modal
      title="短网址"
      open={open}
      onCancel={onCancel}
      footer={
        <Space>
          <Button type="default" onClick={onCancel}>
            关闭
          </Button>
        </Space>
      }
      width={500}
    >
      <div
        className="qr-code-ctn"
        style={{
          marginRight: '34px',
        }}
      >
        {loading ? (
          <p>正在生成短网址...</p>
        ) : shortUrl ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <p className="url" style={{ margin: 0, flex: 1, wordBreak: 'break-all' }}>
                <a href={shortUrl} target="_blank" rel="noopener noreferrer">
                  {shortUrl}
                </a>
              </p>
              <Tooltip title="复制短网址">
                <Button
                  type="primary"
                  onClick={handleCopy}
                  disabled={!shortUrl || loading}
                  loading={loading}
                >
                  复制
                </Button>
              </Tooltip>
            </div>
            <div className="qr-code" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
              <QRCode
                size={128}
                style={{ height: "auto", maxWidth: "40%", width: "40%" }}
                value={shortUrl}
                viewBox={`0 0 128 128`}
              />
            </div>

            <Divider>⬇️⬇️⬇️ 附加功能 ⬇️⬇️⬇️</Divider>
            {/* 统计功能区域 */}
            <div style={{}}>
              {/* 统计功能说明提示 */}
              {!enableStats && (
                <Alert
                  message={
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                        <InfoCircleOutlined style={{ marginRight: '4px', color: '#1890ff' }} />
                        访问统计功能，您可以查看：
                      </div>
                      <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#666' }}>
                        <div>• <strong>总访问量（PV）</strong> - 统计链接的总访问次数</div>
                        <div>• <strong>独立IP数</strong> - 统计有多少个不同的IP访问过</div>
                        <div>• <strong>今日访问量</strong> - 查看今天的访问统计</div>
                        <div>• <strong>省份分布</strong> - 查看访问者来自哪些省份</div>
                        <div>• <strong>浏览器分布</strong> - 查看访问者使用的浏览器类型和版本</div>
                        <div>• <strong>操作系统分布</strong> - 查看访问者的操作系统类型</div>
                        <div>• <strong>设备类型</strong> - 区分移动端和PC端访问</div>
                        <div>• <strong>最近访问记录</strong> - 查看最近的访问详情（IP、时间、来源等）</div>
                      </div>
                    </div>
                  }
                  type="info"
                  showIcon={false}
                  style={{ 
                    marginBottom: '16px',
                    backgroundColor: '#e6f7ff',
                    border: '1px solid #91d5ff',
                  }}
                />
              )}

              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Tooltip title={!isVip ? '仅限VIP用户使用' : ''}>
                  <Switch
                    checked={enableStats}
                    onChange={handleToggleStats}
                    disabled={statsLoading}
                    loading={statsLoading}
                  />
                </Tooltip>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  开启访问统计
                  {!isVip && <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>(VIP)</span>}
                </span>
              </div>
              
              {enableStats && (
                <Alert
                  message="统计功能已开启，系统正在记录访问数据"
                  type="success"
                  showIcon
                  style={{ marginBottom: '12px' }}
                />
              )}

              <Space direction={'horizontal'} align={'start'}>
                <Button
                  type={enableStats ? "primary" : "default"}
                  onClick={handleViewStats}
                  disabled={!enableStats || !isVip}
                  style={{ width: '100%' }}
                >
                  查看统计数据
                </Button>

                {/* 非VIP用户显示购买会员按钮 */}
                {!isVip && (
                  <Button
                    type={'dashed'}
                    onClick={() => setShowCart(true)}
                    style={{ width: '100%', marginBottom: '12px' }}
                  >
                    仅限 VIP 使用，点击购买会员
                  </Button>
                )}
              </Space>
            </div>
          </>
        ) : (
          <p>生成失败，请重试</p>
        )}
      </div>
      
      {/* 统计数据弹框 */}
      {shortId && (
        <ShortUrlStatsModal
          open={statsModalOpen}
          onCancel={() => setStatsModalOpen(false)}
          shortId={shortId}
        />
      )}
      
      {/* VIP 中心弹框 */}
      <Modal 
        title="VIP 中心" 
        footer={null} 
        width="80%" 
        open={showCart} 
        okText="关闭" 
        cancelText="关闭"
        onCancel={() => setShowCart(false)} 
        onOk={() => setShowCart(false)}
      >
        <Cart />
      </Modal>
    </Modal>
  );
};

export default ShortUrlModal;

